import { useDispatch, useSelector } from "react-redux";
import { setSelectedBlockCategory } from "../../store/sceneSlice";
import React, { useEffect, useState } from "react";
import { blocksByCategory } from "../blocks/Block";

import "../../css/CategorySelector.css";

const puzzleBgByCategory = {
  start: "./assets/blocks/start.svg",
  motion: "./assets/blocks/blueCmd.svg",
  looks: "./assets/blocks/looks.svg",
  sound: "./assets/blocks/sounds.svg",
  control: "./assets/blocks/flow.svg",
  end: "./assets/blocks/endshort.svg",
  humandetection: "./assets/blocks/looks.svg",
  facesensing: "./assets/blocks/looks.svg",
  sensors: "./assets/blocks/flow.svg",
  otto: "./assets/blocks/blueCmd.svg",
  esp32: "./assets/blocks/blueCmd.svg",
  robot: "./assets/blocks/blueCmd.svg", // 🤖 ADDED: Robot category
  stembot: "./assets/blocks/looks.svg",
};

export default function CategorySelector({ isPaletteOpen, setIsPaletteOpen }) {
  const dispatch = useDispatch();
  const selectedBlockCategory = useSelector(s => s.scene.selectedBlockCategory);
  const selectedDevice = useSelector(s => s.scene.selectedDevice);
  const isSimulatorOpen = useSelector(s => s.scene.simulatorOpen);
  const isRobotModeOpen = useSelector(s => s.scene.robotModeOpen); // 🤖 ADDED: Robot mode state

  const aiChatOpen = useSelector(s => s.scene.aiChatOpen);
  const [hoverCategory, setHoverCategory] = useState(null);

  // base categories (sensor added conditionally)
  let categories = [
    { id: "start", imgOff: "./assets/categories/StartOff.svg", imgOn: "./assets/categories/StartOn.svg", alt: "Start" },
    { id: "motion", imgOff: "./assets/categories/MotionOff.svg", imgOn: "./assets/categories/MotionOn.svg", alt: "Motion" },
    { id: "looks", imgOff: "./assets/categories/LooksOff.svg", imgOn: "./assets/categories/LooksOn.svg", alt: "Looks" },
    { id: "sound", imgOff: "./assets/categories/SoundOff.svg", imgOn: "./assets/categories/SoundOn.svg", alt: "Sound" },
    { id: "control", imgOff: "./assets/categories/FlowOff.svg", imgOn: "./assets/categories/FlowOn.svg", alt: "Control" },
    { id: "humandetection", imgOff: "./assets/ui/HumanDetection.png", imgOn: "./assets/ui/HumanDetection.png", alt: "Human Detection" },
  ];

  // 🤖 ADDED: Robot category (shows only in robot mode)
  if (isRobotModeOpen) {
    categories.push({
      id: 'robot',
      imgOff: "./assets/categories/Robot.svg",  
      imgOn: "./assets/categories/Robot.svg",    
      alt: "Robot"
    });
  }

  if (aiChatOpen) {
    categories.push({
      id: 'stembot',
      imgOff: "./assets/ui/StembotAI.png", 
      imgOn: "./assets/ui/StembotAI.png",
      alt: "Stembot"
    });
  }
  // Simulator sensor category
  if (isSimulatorOpen) {
    categories.push({
      id: 'sensors',
      imgOff: "./assets/ui/simulator.png",
      imgOn: "./assets/ui/simulator.png",
      alt: "Sensor"
    });
  }

  categories.push({
    id: "end",
    imgOff: "./assets/categories/StopOff.svg",
    imgOn: "./assets/categories/StopOn.svg",
    alt: "End"
  });

  // device-specific categories to append when a device is selected
  const deviceCategories = {
    otto: { id: "otto", imgOff: "./assets/ui/Otto_Bot.png", imgOn: "./assets/ui/Otto_Bot.png", alt: "Otto Robot" },
    esp32: { id: "esp32", imgOff: "./assets/ui/ESP32.png", imgOn: "./assets/ui/ESP32.png", alt: "ESP32" },
  };

  if (selectedDevice && deviceCategories[selectedDevice]) {
    categories = [...categories, deviceCategories[selectedDevice]];
  }

  // PREVIEW BLOCKS based on hover
  const hoverBlocks = hoverCategory ? blocksByCategory[hoverCategory] : [];
  const previewPuzzleBg = hoverCategory ? puzzleBgByCategory[hoverCategory] : null;

  // If simulator closes while Sensor category is active, fall back to Motion and close palette
  useEffect(() => {
    if (!isSimulatorOpen && selectedBlockCategory === 'sensors') {
      dispatch(setSelectedBlockCategory('motion'));
      setIsPaletteOpen(false);
    }
  }, [isSimulatorOpen, selectedBlockCategory, dispatch, setIsPaletteOpen]);

  // 🤖 ADDED: If robot mode closes while Robot category is active, fall back to Motion
  useEffect(() => {
    if (!isRobotModeOpen && selectedBlockCategory === 'robot') {
      dispatch(setSelectedBlockCategory('motion'));
      setIsPaletteOpen(false);
    }
  }, [isRobotModeOpen, selectedBlockCategory, dispatch, setIsPaletteOpen]);

  useEffect(() => {
    if (!aiChatOpen && selectedBlockCategory === 'stembot') {
      dispatch(setSelectedBlockCategory('motion'));
      setIsPaletteOpen(false);
    }
  }, [aiChatOpen, selectedBlockCategory, dispatch, setIsPaletteOpen]);

  return (
    <div className="catbar-tray">
      {categories.map(c => (
        <div
          key={c.id}
          style={{ position: "relative" }}
          onMouseEnter={() => {
            setHoverCategory(c.id);
          }}
          onMouseLeave={() => setHoverCategory(null)}
        >
          <button
            className={`catblockbtn${selectedBlockCategory === c.id ? " active" : ""}`}
            onClick={() => {
              if (selectedBlockCategory === c.id && isPaletteOpen) {
                // Close palette but keep category selected but toggle icon off
                setIsPaletteOpen(false);
              } else {
                // Open palette for this category
                dispatch(setSelectedBlockCategory(c.id));
                setIsPaletteOpen(true);
              }
            }}
            tabIndex={0}
            title={c.alt}
            type="button"
          >
            <img
              className={`catblockicon${selectedBlockCategory === c.id ? " selected" : ""}`}
              src={selectedBlockCategory === c.id && isPaletteOpen ? c.imgOn : c.imgOff}
              alt={c.alt}
              draggable={false}
            />
          </button>
        </div>
      ))}

      {/* HOVER PREVIEW --- RENDERED ONLY ONCE */}
      {hoverCategory && !isPaletteOpen && (
        <div className="hover-block-preview">
          {blocksByCategory[hoverCategory]?.map((b, i) => (
            <div key={i} className="block-palette-tile preview-mode">
              <img className="block-bg" src={puzzleBgByCategory[hoverCategory]} alt="" />
              {b.icon && <img className="block-icon" src={b.icon} alt={b.name} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}





